import type { PiniaPluginContext, StateTree } from "pinia";

// Persists a store's state to Web Storage under its id. Missing, full or refused
// storage leaves the store on its defaults. A store opts in with `persist`:
//
//   persist: true                       whole state, default storage
//   persist: { pick: ["user"] }         only these top-level keys
//   persist: { storage: "local" }       localStorage, to outlive the tab
//   persist: { flush: "sync" }          write at once, before a full page reload
//   persist: { legacy: (read) => ... }  initial state from keys stored before
//   persist: { redact: (state) => ... } strips secrets from what is written

export type StorageName = "local" | "session";

export interface PersistOptions<S extends StateTree = StateTree> {
  pick?: string[];
  storage?: StorageName;
  flush?: "sync" | "pre" | "post";
  legacy?: (read: (key: string) => string | null) => Partial<S> | null;
  // Gets a copy of the picked state and returns what is written.
  redact?: (state: Partial<S>) => Partial<S>;
}

declare module "pinia" {
  // The type parameters must match Pinia's own declaration to merge with it.
  export interface DefineStoreOptionsBase<S extends StateTree, Store> {
    persist?: boolean | PersistOptions<S>;
  }
  export interface PiniaCustomProperties {
    resetAndClearPersisted: () => void;
  }
}

// Reading the storage global throws where storage is blocked, so never read it
// at a module's top level.
export function webStorage(name: StorageName): Storage | null {
  try {
    return (name === "local" ? globalThis.localStorage : globalThis.sessionStorage) ?? null;
  } catch {
    return null;
  }
}

function quietly<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}

/** `storage` is the default for stores that name none. `forget` keys are removed at start-up. */
export function createPersistedState(defaults: { storage?: StorageName; forget?: string[] } = {}) {
  const fallbackName = defaults.storage ?? "local";
  const fallback = webStorage(fallbackName);
  for (const key of defaults.forget ?? []) quietly(() => fallback?.removeItem(key));

  return ({ store, options }: PiniaPluginContext) => {
    const persist: PersistOptions | null = !options.persist ? null : options.persist === true ? {} : options.persist;
    const storage = persist ? webStorage(persist.storage ?? fallbackName) : null;
    const key = store.$id;

    store.resetAndClearPersisted = () => {
      store.$reset();
      quietly(() => storage?.removeItem(key));
    };
    if (!persist || !storage) return;

    const saved = quietly(() => storage.getItem(key));
    try {
      if (saved) {
        store.$patch(JSON.parse(saved));
      } else if (persist.legacy) {
        const carried = persist.legacy((legacyKey) => quietly(() => storage.getItem(legacyKey)));
        if (carried) store.$patch(carried);
      }
    } catch {
      // Unreadable or not JSON: start from the defaults.
    }

    store.$subscribe(
      (_mutation, state) => {
        const kept = persist.pick
          ? Object.fromEntries(persist.pick.filter((k) => k in state).map((k) => [k, state[k]]))
          : state;
        // Round trip so redact works on a copy of the store.
        const written = persist.redact ? persist.redact(JSON.parse(JSON.stringify(kept))) : kept;
        // Full or refused: the next write tries again.
        quietly(() => storage.setItem(key, JSON.stringify(written)));
      },
      // Outlives the component that first used the store.
      { detached: true, flush: persist.flush ?? "pre" },
    );
  };
}
